import type {
  StudentFormRow,
  BatchCreateResponse,
} from '../components/AddStudentsModal/interfaces'

type BatchOutcome = Pick<
  BatchCreateResponse,
  'addedStudents' | 'failedStudents'
>

const hasData = (s: StudentFormRow): boolean =>
  Boolean(s.firstName.trim() || s.lastName.trim())

const normalizeUsername = (username: string): string =>
  username.trim().toLowerCase()

/**
 * Resolves which form rows correspond to the failed students of a batch
 * response. Rows are matched by the batch `index` when present, falling back
 * to a case-insensitive username match. Returns `null` when any failed
 * student cannot be traced back to a row — callers should then keep every
 * row instead of risking removing the wrong one.
 */
export function resolveFailedRowIds(
  submitted: StudentFormRow[],
  failedStudents: BatchCreateResponse['failedStudents'],
): Set<string> | null {
  const ids = new Set<string>()

  for (const failed of failedStudents) {
    let row: StudentFormRow | undefined
    if (failed.index !== null) {
      row = submitted[failed.index]
    } else if (normalizeUsername(failed.username)) {
      row = submitted.find(
        s =>
          normalizeUsername(s.username) ===
          normalizeUsername(failed.username),
      )
    }
    if (!row) return null
    ids.add(row.id)
  }

  return ids
}

/**
 * After a batch submit, removes the rows that were successfully added so a
 * re-submit cannot duplicate them (AR-783). Failed rows and empty rows are
 * kept, and at least one empty row always remains. When nothing was added,
 * or when a failed row cannot be identified, the rows are returned untouched
 * (conservative — better to keep everything than to drop the wrong row).
 */
export function removeSubmittedRows(
  students: StudentFormRow[],
  submitted: StudentFormRow[],
  outcome: BatchOutcome,
  createEmptyRow: () => StudentFormRow,
): StudentFormRow[] {
  if (outcome.addedStudents.length === 0) return students

  const failedRowIds = resolveFailedRowIds(
    submitted,
    outcome.failedStudents,
  )
  if (failedRowIds === null) return students

  const submittedIds = new Set(submitted.map(s => s.id))
  const remaining = students.filter(
    s => !submittedIds.has(s.id) || failedRowIds.has(s.id),
  )

  if (!remaining.some(s => !hasData(s))) {
    remaining.push(createEmptyRow())
  }

  return remaining
}

/**
 * Group ids of the rows sent in a batch, captured at submit time so the
 * success screen keeps showing them after the form rows are cleared.
 */
export function collectSubmittedGroupIds(
  submitted: StudentFormRow[],
): string[] {
  return Array.from(new Set(submitted.flatMap(s => s.groupIds)))
}
